import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchQuestionAnalysis } from '../../api/reports';
import ReportIcon from './ReportIcons';

const LOCKED_ACCESS = {
  level: 'ability_only',
  full_explanations_unlocked: false,
  unlock_condition: 'assessment_completed',
};

export default function QuestionAnalysisSection({ assessmentId, analysisAccess }) {
  const [access, setAccess] = useState(analysisAccess || LOCKED_ACCESS);
  const [status, setStatus] = useState('idle');
  const [payload, setPayload] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState('');
  const controllerRef = useRef(null);
  const requestRef = useRef(0);

  useEffect(() => {
    setAccess(analysisAccess || LOCKED_ACCESS);
  }, [analysisAccess]);

  useEffect(() => {
    requestRef.current += 1;
    controllerRef.current?.abort();
    controllerRef.current = null;
    setStatus('idle');
    setPayload(null);
    setExpanded(false);
    setError('');
  }, [assessmentId]);

  useEffect(() => () => {
    requestRef.current += 1;
    controllerRef.current?.abort();
  }, []);

  const load = useCallback(async () => {
    if (payload) {
      setExpanded((current) => !current);
      return;
    }

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setStatus('loading');
    setError('');

    const result = await fetchQuestionAnalysis(assessmentId, {
      signal: controller.signal,
    });
    if (requestRef.current !== requestId || result.status === 'cancelled') return;

    if (result.status === 'done') {
      setAccess(result.analysisAccess || access);
      setPayload(result.payload);
      setExpanded(true);
      setStatus('done');
      return;
    }
    if (result.status === 'locked') {
      setAccess(result.analysisAccess || LOCKED_ACCESS);
      setStatus('idle');
      return;
    }
    setError(result.error || '逐题解析加载失败，请稍后重试。');
    setStatus('error');
  }, [access, assessmentId, payload]);

  const unlocked = Boolean(access?.full_explanations_unlocked);
  if (!unlocked) {
    return (
      <section className="v5r-analysis-gate" id="v5r-sec-analysis" aria-labelledby="v5r-analysis-title">
        <div className="v5r-analysis-icon" aria-hidden="true">
          <ReportIcon name="lock" size={20} />
        </div>
        <div>
          <div className="v5r-analysis-eyebrow">完成后开放</div>
          <h2 id="v5r-analysis-title">逐题与选项解析</h2>
          <p>完成本次测评后，可回看每道题的选择层级、行为含义和完整解析。</p>
        </div>
      </section>
    );
  }

  const items = Array.isArray(payload?.items) ? payload.items : [];
  return (
    <section
      className="v5r-analysis"
      id="v5r-sec-analysis"
      aria-labelledby="v5r-analysis-title"
      aria-busy={status === 'loading'}
    >
      <div className="v5r-analysis-head">
        <div>
          <div className="v5r-analysis-eyebrow">本次测评已完成</div>
          <h2 id="v5r-analysis-title">逐题与选项解析</h2>
          <p>用冻结的原题和作答记录复盘判断依据，不受后续题库修改影响。</p>
        </div>
        <button
          type="button"
          className="v5r-analysis-trigger"
          onClick={load}
          disabled={status === 'loading'}
          aria-expanded={expanded}
          aria-controls="v5r-analysis-content"
        >
          <ReportIcon name="book-open" size={16} />
          {status === 'loading' ? '正在加载…' : (expanded ? '收起逐题解析' : '查看逐题解析')}
        </button>
      </div>

      {status === 'loading' && (
        <div className="v5r-analysis-status" role="status" aria-live="polite">
          <span className="v5r-state-spin" aria-hidden="true" />
          正在读取冻结作答记录…
        </div>
      )}
      {status === 'error' && (
        <div className="v5r-analysis-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={load}>重试</button>
        </div>
      )}
      {expanded && payload && (
        <div className="v5r-analysis-content" id="v5r-analysis-content">
          <div className="v5r-analysis-summary">
            共 {items.length} 道题；带“我的选择”的选项为本次作答记录。
          </div>
          {items.length === 0 ? (
            <p className="v5r-analysis-empty">暂无可展示的逐题解析。</p>
          ) : (
            <div className="v5r-question-ledger">
              {items.map((item) => (
                <QuestionAnalysisItem key={`${item.question_id}-${item.order_no}`} item={item} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function QuestionAnalysisItem({ item }) {
  const dimensionName = item.dimension?.name || '综合能力';
  const skipped = item.special_option === 'skipped';
  return (
    <details className="v5r-question-item">
      <summary>
        <span className="v5r-question-no">Q{item.q_no}</span>
        <span className="v5r-question-stem">{item.stem || '题目内容暂缺'}</span>
        <span className="v5r-question-dimension">{dimensionName}</span>
      </summary>
      <div className="v5r-question-body">
        {item.scenario_text && (
          <div className="v5r-question-scenario">
            <strong>情境</strong>
            <span>{item.scenario_text}</span>
          </div>
        )}
        {skipped && <div className="v5r-question-skipped">本题作答记录：跳过</div>}
        <ol className="v5r-option-analysis" aria-label={`第 ${item.q_no} 题选项解析`}>
          {(item.options || []).map((option) => (
            <li className={option.is_selected ? 'selected' : ''} key={option.letter}>
              <div className="v5r-option-line">
                <span className="v5r-option-letter">{option.letter}</span>
                <span className="v5r-option-text">{option.text}</span>
                {option.is_selected && <span className="v5r-option-selected">我的选择</span>}
              </div>
              <div className="v5r-option-reading">
                {(option.level_code || option.level_label) && (
                  <span className="v5r-option-level">
                    {[option.level_code, option.level_label].filter(Boolean).join(' · ')}
                  </span>
                )}
                <span>{option.explanation || '本选项暂无补充解析。'}</span>
              </div>
            </li>
          ))}
        </ol>
        {item.question_explanation && (
          <div className="v5r-question-conclusion">
            <strong>本题要点</strong>
            <p>{item.question_explanation}</p>
          </div>
        )}
      </div>
    </details>
  );
}
